'use client';

import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';

import { richTextComponent } from '@/sanity/sanityPortableText';

import type { PortableTextBlock } from 'sanity';

const ease = [0.25, 0.1, 0.25, 1] as const;

export interface PrizeWheelStep {
  _key?: string;
  stepLabel?: string;
  title?: string;
  text?: string;
}

export interface PrizeWheelStepsData {
  stepsChip?: string;
  stepsTitle?: PortableTextBlock[];
  stepsLead?: PortableTextBlock[];
  steps?: PrizeWheelStep[];
}

export default function PrizeWheelStepsSection({
  data,
}: {
  data: PrizeWheelStepsData;
}) {
  const steps = data.steps ?? [];
  if (!data.stepsTitle && !steps.length) return null;

  return (
    <section className="flex w-full flex-col items-center bg-white font-geist max-lg:px-[2rem] max-lg:pb-[8rem] max-lg:pt-0 lg:px-[6rem] lg:py-[15rem]">
      <div className="flex w-[min(170.25rem,100%)] flex-col items-center max-lg:gap-[5rem] lg:gap-[10.5rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease }}
          className="flex flex-col items-center max-lg:gap-[1.5rem] lg:gap-0"
        >
          {data.stepsChip && (
            <p className="mb-0 font-blacker text-[2rem] italic leading-[1.5] text-black">
              {data.stepsChip}
            </p>
          )}
          {data.stepsTitle && (
            <div className="richtext flex flex-col items-center text-center prose-h2:mb-0 prose-h2:font-medium prose-h2:leading-[1.15] max-lg:prose-h2:text-[4.5rem] max-lg:prose-h2:tracking-[-0.225rem] lg:prose-h2:text-[6rem] lg:prose-h2:tracking-[-0.3rem]">
              <PortableText value={data.stepsTitle} components={richTextComponent} />
            </div>
          )}
          {data.stepsLead && (
            <div className="richtext w-[min(88rem,100%)] text-center lg:mt-[1.75rem] prose-p:mb-0 prose-p:[text-wrap:wrap] prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-black/[0.7]">
              <PortableText value={data.stepsLead} components={richTextComponent} />
            </div>
          )}
        </motion.div>

        {!!steps.length && (
          <div className="grid w-full grid-cols-1 lg:grid-cols-3">
            {steps.map((step, i) => (
              <motion.div
                key={step._key ?? i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.8, ease, delay: 0.1 * i }}
                className={`flex flex-col gap-[1.75rem] max-lg:items-center max-lg:text-center lg:items-start lg:px-[4rem] ${
                  i === 0
                    ? 'max-lg:pb-[3rem] lg:pl-0'
                    : 'max-lg:border-t max-lg:border-black/10 max-lg:py-[3rem] lg:border-l lg:border-black/10'
                } ${i === steps.length - 1 ? 'max-lg:pb-0' : ''}`}
              >
                <p className="mb-0 font-geist-mono font-medium uppercase leading-[1.15] text-[#228E71] max-lg:text-[1.75rem] max-lg:tracking-[-0.0525rem] lg:text-[2rem] lg:tracking-[-0.06rem]">
                  {step.stepLabel || `STEP ${String(i + 1).padStart(2, '0')}`}
                </p>
                {step.title && (
                  <h3 className="mb-0 text-[2.75rem] font-medium leading-[1.2] tracking-[-0.06rem] text-black">
                    {step.title}
                  </h3>
                )}
                {step.text && (
                  <p className="mb-0 w-full text-[2.25rem] leading-[1.5] tracking-[-0.1125rem] text-black/[0.6] [text-wrap:wrap] lg:max-w-[39.25rem]">
                    {step.text}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
